import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { TransactionFilters } from '@/api/transactions/use-transactions';

interface TransactionFilterDialogProps {
    isOpen: boolean;
    onClose: () => void;
    filters: TransactionFilters;
    onApplyFilters: (filters: TransactionFilters) => void;
}

const TransactionFilterDialog: React.FC<TransactionFilterDialogProps> = ({
    isOpen,
    onClose,
    filters,
    onApplyFilters,
}) => {
    const [localFilters, setLocalFilters] = useState<TransactionFilters>(filters);

    const handleApply = () => {
        onApplyFilters(localFilters);
        onClose();
    };

    const handleReset = () => {
        const resetFilters: TransactionFilters = {
            customer_id: filters.customer_id,
            page: 1,
            per_page: filters.per_page,
        };
        setLocalFilters(resetFilters);
    };

    const handleClose = () => {
        setLocalFilters(filters);
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Filter Transactions</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="type">Type</Label>
                            <Select
                                value={localFilters.type || 'all'}
                                onValueChange={value =>
                                    setLocalFilters({
                                        ...localFilters,
                                        type:
                                            value === 'all'
                                                ? undefined
                                                : (value as
                                                      | 'deposit'
                                                      | 'withdrawal'
                                                      | 'bonus'
                                                      | undefined),
                                    })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="All types" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All types</SelectItem>
                                    <SelectItem value="deposit">Deposit</SelectItem>
                                    <SelectItem value="withdrawal">Withdrawal</SelectItem>
                                    <SelectItem value="bonus">Bonus</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="status">Status</Label>
                            <Select
                                value={localFilters.status || 'all'}
                                onValueChange={value =>
                                    setLocalFilters({
                                        ...localFilters,
                                        status:
                                            value === 'all'
                                                ? undefined
                                                : (value as
                                                      | 'pending'
                                                      | 'approved'
                                                      | 'rejected'
                                                      | 'completed'
                                                      | 'failed'
                                                      | undefined),
                                    })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="All statuses" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All statuses</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="approved">Approved</SelectItem>
                                    <SelectItem value="rejected">Rejected</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="failed">Failed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="from_date">From Date</Label>
                            <Input
                                type="date"
                                value={localFilters.from_date || ''}
                                onChange={e =>
                                    setLocalFilters({
                                        ...localFilters,
                                        from_date: e.target.value || undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="to_date">To Date</Label>
                            <Input
                                type="date"
                                value={localFilters.to_date || ''}
                                onChange={e =>
                                    setLocalFilters({
                                        ...localFilters,
                                        to_date: e.target.value || undefined,
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="search">Search</Label>
                        <Input
                            placeholder="Search by reference number, description, etc."
                            value={localFilters.search || ''}
                            onChange={e =>
                                setLocalFilters({
                                    ...localFilters,
                                    search: e.target.value || undefined,
                                })
                            }
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="payment_method">Payment Method</Label>
                        <Input
                            placeholder="Filter by payment method"
                            value={localFilters.payment_method || ''}
                            onChange={e =>
                                setLocalFilters({
                                    ...localFilters,
                                    payment_method: e.target.value || undefined,
                                })
                            }
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="sort_by">Sort By</Label>
                            <Select
                                value={localFilters.sort_by || 'default'}
                                onValueChange={value =>
                                    setLocalFilters({
                                        ...localFilters,
                                        sort_by: value === 'default' ? undefined : value,
                                    })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Default" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="default">Default</SelectItem>
                                    <SelectItem value="created_at">Date</SelectItem>
                                    <SelectItem value="amount">Amount</SelectItem>
                                    <SelectItem value="type">Type</SelectItem>
                                    <SelectItem value="status">Status</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="sort_order">Sort Order</Label>
                            <Select
                                value={localFilters.sort_order || 'desc'}
                                onValueChange={value =>
                                    setLocalFilters({
                                        ...localFilters,
                                        sort_order: value as 'asc' | 'desc' | undefined,
                                    })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Descending" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="desc">Descending</SelectItem>
                                    <SelectItem value="asc">Ascending</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleReset}>
                        Reset
                    </Button>
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleApply}>Apply Filters</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TransactionFilterDialog;
